import React, { useEffect, useState } from 'react';
import { Text, View } from 'react-native';
import i18n from 'i18n-js';
import { connect } from 'react-redux';
import moment from 'moment';

import {
  Popup,
  formatSinceTime,
  subStatusName,
  subStatuses,
  convertTime,
  getLinkingModalConfig,
  formatSinceTimeWithDayTranslationCase,
} from '../../../../helpers';
import { scale, fullWidth, is24HourFormat } from '@components/frame';
import { getNCFileByName } from '@reducers/manufacture';

import NCFileType from '@components/common/NCFileType';

import { font as initialFont } from '@styles/fonts';
import Svg from '@styles/icons/svg';
import Button from '@styles/buttons';
import { useForceUpdateEverySecond } from '@v2/pages/common/hooks/useForceUpdateEverySecond';
import NCFileFirstRanInfo from '@components/common/NCFileFirstRanInfo';

const NcFileTime = React.memo(
  ({ C, s, font, subStatus, timeOffset, lastUpdatedOn }) => {
    useForceUpdateEverySecond();

    const time = moment.utc().add(timeOffset, 'milliseconds');

    const lastModifiedMilliseconds = moment.utc(lastUpdatedOn).valueOf();

    const lastTime = Math.round((time.utc().valueOf() - lastModifiedMilliseconds) / 1000);

    const isMoreHour = moment.utc().diff(moment.utc(lastUpdatedOn), 'm') >= 60;
    const isRunning = subStatus === subStatuses.Running;
    return (
      <Text
        style={[
          {
            color: C('Label/Dark/Secondary'),
            marginRight: s(4),
            fontVariant: ['tabular-nums'],
          },
          font['Subhead 1'],
        ]}
      >
        {isRunning || !isMoreHour
          ? `${convertTime(lastTime)} ${i18n.t(subStatusName[subStatus] || subStatus)}`
          : `${formatSinceTimeWithDayTranslationCase(lastUpdatedOn, is24HourFormat)}`}
      </Text>
    );
  },
  (prev, next) =>
    prev.timeOffset === next.timeOffset &&
    prev.subStatus === next.subStatus &&
    prev.lastUpdatedOn === next.lastUpdatedOn &&
    prev.C.scheme === next.C.scheme
);

const NCFileIgnored = ({
  selectedItem: { machineStatus },
  selectedItem,
  noSelectedNCFileSubStatusButNCFileExist,
  C,
  darkMode = false,
  font = initialFont,

  getNCFileByName,
  timeOffset,
}) => {
  const { ncFile } = machineStatus || {};
  const lineSize = 250;
  const s = scale;
  const [additionalInfo, setAdditionalInfo] = useState({});
  useEffect(() => {
    (async () => {
      const data = await getNCFileByName(machineStatus.ncFile);
      if (data?.searchResult?.[0]) {
        setAdditionalInfo(data?.searchResult?.[0]);
      }
    })();
  }, [machineStatus.ncFile]);

  const submitConfig = darkMode
    ? {
        variant: 'Dialog L',
        type: 'Secondary',
        background: 'Dark',
      }
    : {
        variant: 'L',
        type: 'Only Text Link',
      };

  return (
    <View
      style={{
        marginTop: s(7),
        width: s(360),
        alignItems: 'center',
        alignContent: 'center',
        justifyContent: 'center',
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          alignContent: 'center',
          justifyContent: 'center',
        }}
      >
        <View style={{ marginRight: s(3) }}>
          <Svg icon="machineIgnoredSmall" width={s(15)} height={s(12)} color="Label/Dark/Secondary" includeScheme={!darkMode} key={s(15)} />
        </View>
        <Text style={[{ color: C('Label/Dark/Secondary') }, font.Body]}>{i18n.t('Ignored NC File')}</Text>
      </View>
      <View style={{ marginTop: s(16) }}>
        <Text style={[{ color: C('Label/Dark/Primary') }, font.Headline]}>{ncFile} </Text>
      </View>
      <View style={{ marginTop: s(8) }}>
        {machineStatus.subStatus && (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              alignContent: 'center',
              justifyContent: 'center',
            }}
          >
            <NcFileTime
              {...{
                subStatus: machineStatus.subStatus,
                lastUpdatedOn: machineStatus.lastUpdatedOn,
                timeOffset,
                lineSize,
                font,
                C,
                s,
              }}
            />
          </View>
        )}
      </View>
      <NCFileFirstRanInfo firstRanByMachineName={additionalInfo.firstRanByMachineName} firstRanOn={additionalInfo.firstRanOn} />
      {noSelectedNCFileSubStatusButNCFileExist && (
        <View style={{ marginTop: s(16) }}>
          <Text style={[{ color: C('Label/Dark/Secondary'), textAlign: 'center' }, font['Subhead 1']]}>
            {i18n.t('Sent by machine as Ignored NC File')}
          </Text>
        </View>
      )}
      {!noSelectedNCFileSubStatusButNCFileExist && (
        <View style={{ marginTop: s(16) }}>
          <Text style={[{ color: C('Label/Dark/Secondary'), textAlign: 'center' }, font['Subhead 1']]}>
            {i18n.t('Classified as Ignored NC File')}
          </Text>
          <Text style={[{ color: C('Label/Dark/Secondary'), textAlign: 'center' }, font['Subhead 1']]}>
            {i18n.t('on')} {formatSinceTime(additionalInfo.ncFileStatusSetOn)}
          </Text>
          <Text style={[{ color: C('Label/Dark/Secondary'), textAlign: 'center' }, font['Subhead 1']]}>
            {i18n.t('by')} {additionalInfo.ncFileStatusSetBy}
          </Text>
        </View>
      )}
      {!noSelectedNCFileSubStatusButNCFileExist && (
        <View style={{ marginTop: s(16), justifyContent: 'center' }}>
          <Button
            {...{
              ...submitConfig,
              onPress: () =>
                Popup({
                  Component: (p) => <NCFileType {...{ ...p, selectedItem, context: 'batch' }} />,
                  ...getLinkingModalConfig(scale, fullWidth),
                }),
              font,
            }}
          >
            {i18n.t('Modify NC File Type')}
          </Button>
        </View>
      )}
    </View>
  );
};

export default connect(({ machines: { timeOffset } }) => ({ timeOffset }), { getNCFileByName })(NCFileIgnored);
